import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import MultiSelect from 'components/common/MultiSelect';
import FailedRequest from 'components/requests-response/FailedRequest';
import SuccessRequest from 'components/requests-response/SuccessRequest';
import useApi from 'helpers/api';
import { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import EmailNotificationTemplateService from 'services/EmailNotificationTemplate.service';
import EmailEditor from './EmailNotificationEditor';
import PlaceholderAutocomplete from './PlaceholderAutocomplete';
import PlaceholderSelector from './PlaceholderSelector';

const ASSIGNED_AGENT_VALUE = 'ASSIGNED_AGENT';

const EmailNotificationEdit = () => {
  const { id } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const placeholderSelectorRef = useRef(null);

  const [body, setBody] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [error, setError] = useState({});
  const [focusedInput, setFocusedInput] = useState(null);

  useEffect(() => {
    getNotification();
  }, [id]);

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    console.log(body);
  }, [body]);

  // Update body recipients when selectedUsers changes
  useEffect(() => {
    const recipients = selectedUsers.map(item => {
      if (item.value === ASSIGNED_AGENT_VALUE) {
        return {
          recipientTarget: 'TICKET_ASSIGNED_AGENT',
          userId: null
        };
      }
      return {
        recipientTarget: 'SPECIFIC_AGENT',
        userId: item.value
      };
    });
    setBody(prev => ({ ...prev, recipients }));
  }, [selectedUsers]);

  useEffect(() => {
    const handleKeyPress = e => {
      if (e.ctrlKey && e.code === 'Space') {
        if (placeholderSelectorRef.current) {
          e.preventDefault();
          if (placeholderSelectorRef.current.isOpen()) {
            placeholderSelectorRef.current.close();
          } else {
            placeholderSelectorRef.current.open();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();

    const payload = {
      subjectTemplate: body.subjectTemplate,
      descriptionTemplate: body.descriptionTemplate,
      inactive: body.inactive,
      recipients: body.recipients || [],
      notifyPreticketRegisteredContactsOnly:
        body.notifyPreticketRegisteredContactsOnly
    };

    const response = await EmailNotificationTemplateService.edit(id, payload);
    if (response.id) {
      // Refresh local state with response (mapping back to UI state)
      // Note: response might not include full recipients details depending on API,
      // but we should handle it if it does or just use local state + success.
      // Assuming response follows Output DTO which might be minimal.
      // Let's rely on getNotification to re-fetch full details or just keep current if it matches.
      // But typically we update body with response.
      // Since Output DTO is different from Input, we might need to be careful.
      // The user said Output DTO has: id, name, inactive, recipientType.
      // It doesn't explicitly list recipients. If recipients are returned, we map them.
      // If not, we might want to re-fetch or trust our local state.
      // Let's re-fetch to be safe and consistent.
      await getNotification();
      setShowSuccess(true);
    } else {
      setError(response);
      setShowError(true);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setBody({ ...body, [name]: value });
  };

  const getNotification = async () => {
    const response = await EmailNotificationTemplateService.find(id);
    if (response.id) {
      setBody(response);
      handleAgents(response);
    }
  };

  const getUsers = async () => {
    const response = await api.agents.listSummaryData();
    const assignedAgentOption = {
      value: ASSIGNED_AGENT_VALUE,
      label: 'Agente Atribuído'
    };

    if (response.length > 0) {
      const formattedUsers = response.map(user => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName || ''}`.trim()
      }));
      setUsers([assignedAgentOption, ...formattedUsers]);
    } else {
      setUsers([assignedAgentOption]);
    }
  };

  const handleAgents = response => {
    // Map response.recipients to selectedUsers
    // Assuming response.recipients exists and follows the structure
    if (response.recipients && response.recipients.length > 0) {
      const selected = response.recipients
        .map(recipient => {
          if (recipient.recipientTarget === 'TICKET_ASSIGNED_AGENT') {
            return {
              value: ASSIGNED_AGENT_VALUE,
              label: 'Agente Atribuído'
            };
          } else if (
            recipient.recipientTarget === 'SPECIFIC_AGENT' &&
            recipient.user
          ) {
            return {
              value: recipient.user.id,
              label: `${recipient.user.firstName} ${
                recipient.user.lastName || ''
              }`.trim()
            };
          }
          return null;
        })
        .filter(Boolean);
      setSelectedUsers(selected);
    } else {
      setSelectedUsers([]);
    }
  };

  // Need to update selectedUsers labels once users are loaded if they were just IDs
  useEffect(() => {
    if (users.length > 0 && selectedUsers.length > 0) {
      setSelectedUsers(prev =>
        prev.map(sel => {
          const found = users.find(u => u.value === sel.value);
          if (found) return found;
          return sel;
        })
      );
    }
  }, [users]); // Be careful with loops here, but users usually sets once.

  const handleInputFocus = event => {
    setFocusedInput(event.target);
  };

  const handleInsertPlaceholder = placeholder => {
    if (focusedInput && focusedInput.name === 'subjectTemplate') {
      const start = focusedInput.selectionStart;
      const end = focusedInput.selectionEnd;
      const value = focusedInput.value;

      const newValue =
        value.substring(0, start) + placeholder + value.substring(end);

      focusedInput.value = newValue;

      focusedInput.focus();
      focusedInput.setSelectionRange(
        start + placeholder.length,
        start + placeholder.length
      );

      setBody({ ...body, subjectTemplate: newValue });
    } else {
      if (editorRef.current && editorRef.current.insertContent) {
        editorRef.current.insertContent(placeholder);
      } else {
        const currentDescription = body.descriptionTemplate || '';
        const newDescription = currentDescription + placeholder;
        setBody({ ...body, descriptionTemplate: newDescription });
      }
    }
  };

  return (
    <>
      <SuccessRequest
        showAlert={showSuccess}
        setShowAlert={() => setShowSuccess(false)}
        message="Template de notificação editado com sucesso"
      />
      <FailedRequest
        showAlert={showError}
        setShowAlert={() => setShowError(false)}
        message={error.userMessage}
      />
      <Row className="g-2">
        <Col xl={12}>
          <FalconComponentCard>
            <Col>
              <Flex justifyContent={'between'} alignItems={'center'}>
                <Flex alignItems={'center'}>
                  <IconButton
                    onClick={() => navigate(-1)}
                    variant="falcon-default"
                    size="sm"
                    icon="arrow-left"
                    className="me-1 ms-3"
                  />
                  <FalconComponentCard.Header
                    className="py-3 px-1"
                    title={`Notificação de ${
                      body.recipientType === 'AGENT' ? 'Agente' : 'Solicitante'
                    } / ${body.name}`}
                  />
                </Flex>

                <Flex alignItems={'center'} className={'me-2'}>
                  <Form.Label className="mb-0 me-2">Notificar</Form.Label>
                  <Form.Check
                    type="switch"
                    checked={!body.inactive}
                    onChange={() =>
                      setBody({ ...body, inactive: !body.inactive })
                    }
                  />
                </Flex>
              </Flex>
            </Col>
            <FalconComponentCard.Body>
              <Form onSubmit={handleSubmit} className="pb-2">
                <Form.Group className="mb-3" controlId="subject">
                  <Col>
                    <Flex justifyContent={'between'}>
                      <Form.Label>
                        Assunto <span className="text-danger"> *</span>
                      </Form.Label>
                    </Flex>
                  </Col>
                  <Form.Control
                    type="text"
                    placeholder="Assunto"
                    name="subjectTemplate"
                    value={body.subjectTemplate}
                    onChange={handleChange}
                    onFocus={handleInputFocus}
                    required
                  />
                </Form.Group>
                <Form.Group className="mb-2" controlId="message">
                  <Form.Label className="mb-1 fs-10 d-flex align-items-center justify-content-between gap-2">
                    <span>
                      Descrição <span className="text-danger"> *</span>
                    </span>
                    <PlaceholderSelector
                      ref={placeholderSelectorRef}
                      onInsertPlaceholder={handleInsertPlaceholder}
                    />
                  </Form.Label>
                  <div className="position-relative">
                    <EmailEditor
                      theme="snow"
                      onFocus={handleInputFocus}
                      ref={editorRef}
                      data={body.descriptionTemplate}
                      value={value =>
                        setBody({ ...body, descriptionTemplate: value })
                      }
                    />
                    <PlaceholderAutocomplete
                      editorRef={editorRef}
                      onInsertPlaceholder={() => {}}
                    />
                  </div>
                </Form.Group>
                <Row className="align-items-end">
                  {body.recipientType === 'AGENT' &&
                  body.eventType === 'PRE_TICKET_CREATED' ? (
                    <>
                      <Col md={6}>
                        <Form.Group controlId="people">
                          <Form.Label>Notificar Agentes</Form.Label>
                          <MultiSelect
                            placeholder="Selecione os Agentes"
                            options={users}
                            value={selectedUsers}
                            onChange={values => setSelectedUsers(values)}
                          />
                        </Form.Group>
                      </Col>

                      <Col md={6}>
                        <Form.Group>
                          <Form.Switch
                            label="Notificar apenas pré-tickets de contatos registrados"
                            name="notifyPreticketRegisteredContactsOnly"
                            checked={body.notifyPreticketRegisteredContactsOnly}
                            onChange={() =>
                              setBody({
                                ...body,
                                notifyPreticketRegisteredContactsOnly:
                                  !body.notifyPreticketRegisteredContactsOnly
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                    </>
                  ) : body.recipientType === 'AGENT' ? (
                    <Col md={6}>
                      <Form.Group controlId="people">
                        <Form.Label>Notificar Agentes</Form.Label>
                        <MultiSelect
                          placeholder="Selecione os Agentes"
                          options={users}
                          value={selectedUsers}
                          onChange={values => setSelectedUsers(values)}
                        />
                      </Form.Group>
                    </Col>
                  ) : (
                    <Col md={6}></Col>
                  )}
                  <Col md={12}>
                    <Flex justifyContent="end" className="gap-2">
                      <Button
                        size="sm"
                        variant="falcon-default"
                        as={Link}
                        to="/settings/email-notifications"
                      >
                        Cancelar
                      </Button>
                      <Button type="submit" size="sm">
                        Salvar
                      </Button>
                    </Flex>
                  </Col>
                </Row>
              </Form>
            </FalconComponentCard.Body>
          </FalconComponentCard>
        </Col>
      </Row>
    </>
  );
};

export default EmailNotificationEdit;
